import React, { useMemo } from 'react';
import { Badge, Box, Flex, Text, Tooltip } from '@chakra-ui/react';
import { Info, Warning } from 'phosphor-react';

import { GlassCard } from '../../../../components/glass';
import { IComparativoFranquia, IFolhaFilial } from '../../../../hooks/useFinanceiro';
import { AnimatedBar } from '../components/AnimatedBar';
import { brl } from '../components/format';

interface PorFilialProps {
  filiais: IFolhaFilial[];
  comparativo: IComparativoFranquia[];
}

/**
 * Cruzamento honesto custo (CA, por conta_origem) × quadro (Domínio, por filial).
 * O custo de pessoal é lançado por `conta_origem` no Conta Azul; o colaborador é
 * registrado por `external_id` (filial) no Domínio. Nem sempre coincidem — algumas
 * bases concentram o custo sem ter o quadro ativo (e vice-versa). Mostramos lado a
 * lado, sem inventar um "custo por cabeça por filial" que enganaria.
 */
export const PorFilial = ({ filiais, comparativo }: PorFilialProps) => {
  const linhas = useMemo(() => {
    const filMap = new Map(filiais.map(f => [f.franquia, f]));
    const compMap = new Map(comparativo.map(c => [c.franquia, c]));
    const nomes = Array.from(
      new Set([...comparativo.map(c => c.franquia), ...filiais.map(f => f.franquia)]),
    );
    return nomes
      .map(nome => {
        const fil = filMap.get(nome);
        return {
          nome,
          caCusto: compMap.get(nome)?.pessoal ?? 0,
          headcount: fil?.qtd ?? 0,
          folha: fil?.salario_total ?? 0,
        };
      })
      .sort((a, b) => b.caCusto - a.caCusto);
  }, [filiais, comparativo]);

  const maxCusto = Math.max(1, ...linhas.map(l => l.caCusto));
  const nDivergentes = linhas.filter(l => l.caCusto > 0 && l.headcount === 0).length;

  return (
    <GlassCard intensity="strong" p={{ base: 4, md: 6 }}>
      <Flex align="center" gap={2} mb={1}>
        <Text fontWeight={700} color="#0F172A">
          Custo × quadro por filial
        </Text>
        <Tooltip
          hasArrow
          placement="top"
          bg="#0F172A"
          color="white"
          p={3}
          borderRadius="md"
          maxW="340px"
          label="O custo vem do Conta Azul (por conta_origem, no período). O quadro vem do Domínio (por filial / external_id, snapshot atual). Onde há custo sem quadro, a folha é lançada numa base e o colaborador está registrado em outra."
        >
          <Box as="span" cursor="help" color="#94A3B8">
            <Info size={15} />
          </Box>
        </Tooltip>
      </Flex>
      <Text fontSize="11px" color="#94A3B8" mb={5}>
        Custo de pessoal (CA, período) vs colaboradores e folha registrados (Domínio)
        {nDivergentes > 0 && (
          <>
            {' · '}
            <Box as="span" color="#D97706" fontWeight={600}>
              {nDivergentes} base(s) com custo sem quadro ativo
            </Box>
          </>
        )}
      </Text>

      {/* Cabeçalho */}
      <Flex
        fontSize="10px"
        fontWeight={700}
        color="#94A3B8"
        textTransform="uppercase"
        letterSpacing="0.04em"
        px={1}
        pb={2}
        gap={3}
      >
        <Text flex="1">Filial</Text>
        <Text w={{ base: '110px', md: '150px' }} textAlign="right">
          Custo CA (período)
        </Text>
        <Text w="70px" textAlign="right" display={{ base: 'none', sm: 'block' }}>
          Quadro
        </Text>
        <Text w={{ base: '90px', md: '110px' }} textAlign="right">
          Folha Domínio
        </Text>
      </Flex>

      <Flex direction="column" gap={3}>
        {linhas.map(l => {
          const semQuadro = l.caCusto > 0 && l.headcount === 0;
          const semCusto = l.headcount > 0 && l.caCusto === 0;
          return (
            <Box key={l.nome}>
              <Flex align="center" gap={3}>
                <Flex flex="1" align="center" gap={2} minW={0}>
                  <Text color="#0F172A" fontWeight={600} noOfLines={1}>
                    {l.nome}
                  </Text>
                  {semQuadro && (
                    <Badge
                      display="flex"
                      alignItems="center"
                      gap={1}
                      colorScheme="orange"
                      fontSize="9px"
                      variant="subtle"
                      whiteSpace="nowrap"
                    >
                      <Warning size={9} weight="fill" /> custo sem quadro
                    </Badge>
                  )}
                  {semCusto && (
                    <Badge colorScheme="blue" fontSize="9px" variant="subtle">
                      quadro sem custo
                    </Badge>
                  )}
                </Flex>
                <Text
                  w={{ base: '110px', md: '150px' }}
                  textAlign="right"
                  fontWeight={700}
                  color="#0F172A"
                  whiteSpace="nowrap"
                >
                  {brl(l.caCusto)}
                </Text>
                <Text
                  w="70px"
                  textAlign="right"
                  color={l.headcount === 0 ? '#D97706' : '#0F172A'}
                  display={{ base: 'none', sm: 'block' }}
                >
                  {l.headcount}
                </Text>
                <Text
                  w={{ base: '90px', md: '110px' }}
                  textAlign="right"
                  color="#64748B"
                  whiteSpace="nowrap"
                >
                  {l.folha > 0 ? brl(l.folha) : '—'}
                </Text>
              </Flex>
              <Box mt={1.5}>
                <AnimatedBar
                  pct={l.caCusto > 0 ? Math.max(3, (l.caCusto / maxCusto) * 100) : 0}
                  cor={semQuadro ? '#D97706' : '#DB2777'}
                  h="5px"
                />
              </Box>
            </Box>
          );
        })}
        {linhas.length === 0 && (
          <Text fontSize="sm" color="#94A3B8" textAlign="center" py={6}>
            Sem dados de custo ou quadro por filial no período.
          </Text>
        )}
      </Flex>
    </GlassCard>
  );
};
